import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Screen from '../../components/common/Screen';
import Title from '../../components/common/Title';
import Button from '../../components/elements/Button';
import InputField from '../../components/forms/InputField';
import Toast from '../../components/forms/Toast';
import Icon from '../../components/common/Icon';
import { apiRequest, fetchUserInfo } from '../../utils';

// Change the account's email address. The address is also the login username, so
// the change only lands after the user clicks a confirmation link mailed to the
// NEW address (handled server-side + on /confirm-email-change). This screen just
// kicks that off — nothing changes here until the link is clicked.
const ChangeEmail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState(null);

  useEffect(() => {
    fetchUserInfo('/profile', setUserInfo, setLoading, setError);
  }, []);

  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    const formData = new FormData(event.target);
    const { new_email } = Object.fromEntries(formData.entries());

    try {
      await apiRequest('POST', '/request-email-change', { new_email });
      setError(null);
      setSentTo(new_email);
    } catch (err) {
      setError(err.message);
      setShowToast(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Screen color="white" p="3">
      <div className="top">
        <Title>{t('changeEmail')}</Title>

        {sentTo ? (
          <div className="text-center mt-4">
            <div style={{ fontSize: '2.5rem' }} className="mb-2">
              <Icon name="paper-plane" theme="primary" />
            </div>
            <p className="fw-bold">{t('emailChangeSentTo', { email: sentTo })}</p>
            <p className="text-secondary">{t('emailChangeInstructions')}</p>
          </div>
        ) : (
          <form onSubmit={onSubmit}>
            <p className="text-secondary mb-4">{t('changeEmailSubtitle')}</p>

            <div className="mb-3 text-start">
              <label className="input-label" style={{ color: '#6b7280', fontWeight: 500 }}>
                {t('currentEmail')}
              </label>
              <div className="text-secondary">
                {loading ? '…' : userInfo?.email}
              </div>
            </div>

            <InputField
              type="email"
              name="new_email"
              label={t('newEmail')}
              placeholder={t('enterNewEmail')}
              maxLength={120}
              required
            />

            <Button type="submit" className="mt-2" disabled={submitting}>
              {submitting ? '…' : t('sendConfirmation')}
            </Button>
          </form>
        )}

        <Toast showToast={showToast} setShowToast={setShowToast} message={t(error)} />
      </div>

      <div className="bottom">
        <div className="px-3">
          <hr className="mb-3" />
          <Button theme="light" onClick={() => navigate('/profile')} className="mb-3">
            {t('done')}
          </Button>
        </div>
      </div>
    </Screen>
  );
};

export default ChangeEmail;
